import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ReportFiltersContainer from './ReportFilters/ReportFiltersContainer';
import ReportParameterContainer from './ReportParameters/ReportParameterContainer';
import UserGroupListContainer from '../../../common/components/userGroup/userGroupList/UserGroupListContainer';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import ReportGeneralDataContainer from './GeneralData/ReportGeneralDataContainer';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { REPORT_QUERY } from './queries';
import { SAVE_REPORTS_X_USER_GROUPS } from './mutations';
import { REPORT_X_USER_GROUPS_UPDATED } from './subscriptions';

import useLanguageTracker from 'hooks/useLanguageTracker';
import { useToast } from 'hooks/toasts/useToast';

const ReportContainer = ({ reportId, onChangeReportItemTitle, onRedirect }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  // #region Queries

  const { data, loading, refetch } = useQuery(REPORT_QUERY, {
    variables: { reportId: reportId },
    skip: reportId === undefined
  });

  // #endregion

  // #region Mutations

  const [saveReportXUserGroups] = useMutation(SAVE_REPORTS_X_USER_GROUPS);

  // #endregion

  // #region Subscriptions

  useSubscription(REPORT_X_USER_GROUPS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportXUserGroupsUpdated'), 'success');
      client.cache.evict({
        id: `ReportItem:{"reportId":${data.reportXUserGroupsUpdated.reportId}}`
      });
    }
  });

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  // #endregion

  // #region Handlers

  const onReportXUserGroupsSave = selectedUserGroups => {
    const reportXUserGroups = selectedUserGroups.map(item => {
      return {
        reportId: reportId,
        userGroupId: item.userGroupId
      };
    });
    const input = { reportXUserGroups, reportId };
    saveReportXUserGroups({ variables: { input } });
  };

  // #endregion

  return loading ? (
    <Card>
      <FakeText lines={10} />
    </Card>
  ) : (
    <>
      <ReportGeneralDataContainer
        data={data?.report}
        reportId={reportId}
        onRedirect={onRedirect}
        onChangeReportItemTitle={onChangeReportItemTitle}
      />
      {reportId !== undefined ? (
        <>
          <ReportParameterContainer data={data?.report?.reportParameters ?? []} reportId={reportId} />
          <ReportFiltersContainer data={data?.report?.reportFilters ?? []} reportId={reportId} />
          <UserGroupListContainer
            xUserGroups={data?.report?.reportXUserGroups ?? []}
            onXUserGroupsSave={onReportXUserGroupsSave}
            parentLoading={loading}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
};

ReportContainer.propTypes = {
  reportId: PropTypes.number,
  onChangeReportItemTitle: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired
};

export default ReportContainer;
